/**
 * Ton.
 *
 * Alles wird zur Laufzeit erzeugt, keine Datei wird geladen. Zwei Gruende: die
 * Auslieferung bleibt eine einzige statische Seite ohne Anhaenge, und ein Klang, der
 * aus wenigen Zeilen entsteht, laesst sich anpassen, ohne eine Aufnahme zu suchen.
 *
 * Der Browser laesst Ton erst nach einer Benutzeraktion zu. Der Klick auf
 * "Partie beginnen" ist diese Aktion; davor wird nichts erzeugt.
 */

export type Klangname =
  | 'wuerfel'
  | 'muenzen'
  | 'bauen'
  | 'eingang'
  | 'gebot'
  | 'ausgeschieden'
  | 'sieg'

const SPEICHER = 'fuenf-sterne.ton'

let kontext: AudioContext | null = null
let summe: GainNode | null = null
let an = leseEinstellung()

function leseEinstellung(): boolean {
  try {
    return localStorage.getItem(SPEICHER) !== 'aus'
  } catch {
    return true
  }
}

export function tonAn(): boolean {
  return an
}

export function tonUmschalten(): boolean {
  an = !an
  try {
    localStorage.setItem(SPEICHER, an ? 'ein' : 'aus')
  } catch {
    // Ohne Speicher gilt die Einstellung eben nur fuer diese Sitzung.
  }
  return an
}

/** Muss aus einer Benutzeraktion heraus aufgerufen werden. */
export function tonVorbereiten(): void {
  if (kontext) return
  try {
    kontext = new AudioContext()
    summe = kontext.createGain()
    summe.gain.value = 0.32
    summe.connect(kontext.destination)
  } catch {
    kontext = null
  }
}

interface TonVorgabe {
  readonly form: OscillatorType
  readonly von: number
  readonly bis: number
  readonly dauer: number
  readonly lautstaerke: number
  readonly verzoegerung: number
}

function ton(v: Partial<TonVorgabe> & { von: number }): void {
  if (!kontext || !summe) return
  const {
    form = 'sine',
    von,
    bis = von,
    dauer = 0.16,
    lautstaerke = 0.5,
    verzoegerung = 0,
  } = v
  const t = kontext.currentTime + verzoegerung
  const o = kontext.createOscillator()
  const g = kontext.createGain()
  o.type = form
  o.frequency.setValueAtTime(von, t)
  if (bis !== von) o.frequency.exponentialRampToValueAtTime(Math.max(1, bis), t + dauer)
  g.gain.setValueAtTime(0.0001, t)
  g.gain.exponentialRampToValueAtTime(lautstaerke, t + 0.012)
  g.gain.exponentialRampToValueAtTime(0.0001, t + dauer)
  o.connect(g).connect(summe)
  o.start(t)
  o.stop(t + dauer + 0.02)
}

/** Kurzes Rauschen - Wuerfel, die ueber den Tisch rollen. */
function rauschen(dauer: number, lautstaerke: number, verzoegerung = 0): void {
  if (!kontext || !summe) return
  const bilder = Math.floor(kontext.sampleRate * dauer)
  const puffer = kontext.createBuffer(1, bilder, kontext.sampleRate)
  const daten = puffer.getChannelData(0)
  for (let i = 0; i < bilder; i++) {
    daten[i] = (Math.random() * 2 - 1) * (1 - i / bilder)
  }
  const quelle = kontext.createBufferSource()
  quelle.buffer = puffer
  const filter = kontext.createBiquadFilter()
  filter.type = 'bandpass'
  filter.frequency.value = 2200
  filter.Q.value = 0.9
  const g = kontext.createGain()
  g.gain.value = lautstaerke
  quelle.connect(filter).connect(g).connect(summe)
  quelle.start(kontext.currentTime + verzoegerung)
}

export function spieleKlang(name: Klangname): void {
  if (!an) return
  tonVorbereiten()
  if (!kontext) return
  if (kontext.state === 'suspended') void kontext.resume()

  switch (name) {
    case 'wuerfel':
      rauschen(0.16, 0.5)
      rauschen(0.1, 0.35, 0.13)
      ton({ form: 'triangle', von: 320, bis: 190, dauer: 0.1, lautstaerke: 0.22, verzoegerung: 0.22 })
      break

    case 'muenzen':
      ton({ form: 'triangle', von: 1180, dauer: 0.1, lautstaerke: 0.3 })
      ton({ form: 'triangle', von: 1560, dauer: 0.12, lautstaerke: 0.26, verzoegerung: 0.06 })
      ton({ form: 'triangle', von: 1980, dauer: 0.16, lautstaerke: 0.2, verzoegerung: 0.12 })
      break

    case 'bauen':
      ton({ form: 'square', von: 140, bis: 70, dauer: 0.14, lautstaerke: 0.3 })
      rauschen(0.12, 0.28, 0.02)
      ton({ form: 'sine', von: 520, bis: 780, dauer: 0.22, lautstaerke: 0.16, verzoegerung: 0.1 })
      break

    case 'eingang':
      ton({ form: 'sine', von: 880, dauer: 0.14, lautstaerke: 0.26 })
      ton({ form: 'sine', von: 1320, dauer: 0.2, lautstaerke: 0.2, verzoegerung: 0.07 })
      break

    case 'gebot':
      ton({ form: 'triangle', von: 700, dauer: 0.07, lautstaerke: 0.24 })
      break

    case 'ausgeschieden':
      ton({ form: 'sawtooth', von: 420, bis: 110, dauer: 0.6, lautstaerke: 0.24 })
      break

    case 'sieg':
      for (const [i, f] of [523, 659, 784, 1047].entries()) {
        ton({ form: 'triangle', von: f, dauer: 0.34, lautstaerke: 0.26, verzoegerung: i * 0.11 })
      }
      break
  }
}
